import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, apiClient } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Phone, Search, Calendar, User, Filter, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const DEPT_COLORS = {
    cs: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    mentor: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
    sales: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
    bd: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    unknown: 'bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-300',
};

function getDept(role) {
    if (!role) return 'unknown';
    if (role.includes('cs') || role === 'cs_agent' || role === 'cs_head') return 'cs';
    if (role.includes('mentor') || role.includes('academic')) return 'mentor';
    if (role.includes('sales') || role === 'team_leader') return 'sales';
    if (role.includes('business_development') || role.includes('bd')) return 'bd';
    return 'unknown';
}

export default function SharedCallLogPage() {
    const { user } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [agents, setAgents] = useState([]);
    const [filterAgent, setFilterAgent] = useState('all');
    const [filterDept, setFilterDept] = useState('all');
    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().slice(0, 10);
    });
    const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
    const [searchTerm, setSearchTerm] = useState('');
    const [syncing, setSyncing] = useState(false);

    const isAdmin = ['super_admin', 'admin', 'cs_head'].includes(user?.role);

    const handleForceSync = async () => {
        setSyncing(true);
        try {
            const res = await apiClient.post('/3cx/force-sync-initiated', { hours: 48 });
            const d = res.data;
            toast.success(`Synced ${d.resolved} calls, cleaned ${d.cleaned} stale. ${d.remaining_initiated} still pending.`);
            fetchLogs();
        } catch { toast.error('Sync failed'); }
        setSyncing(false);
    };

    const fetchLogs = useCallback(async () => {
        try {
            setLoading(true);
            const params = { date_from: dateFrom, date_to: dateTo };
            if (filterAgent !== 'all') params.agent_id = filterAgent;
            if (filterDept !== 'all') params.department = filterDept;
            if (searchTerm) params.search = searchTerm;
            const res = await apiClient.get('/shared-call-log', { params });
            setLogs(res.data?.logs || []);
        } catch {
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, [dateFrom, dateTo, filterAgent, filterDept, searchTerm]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    useEffect(() => {
        if (isAdmin) {
            apiClient.get('/users?page_size=200').then(r => {
                const items = r.data?.items || [];
                const csAndMentors = items.filter(u =>
                    ['cs_agent', 'cs_head', 'mentor', 'academic_master', 'master_of_academics', 'master_of_academics_'].includes(u.role)
                );
                setAgents(csAndMentors);
            }).catch(() => {});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Find duplicate calls (same student called by different people same day)
    const duplicateMap = {};
    logs.forEach(log => {
        const key = `${log.student_phone}_${log.date}`;
        if (!duplicateMap[key]) duplicateMap[key] = [];
        duplicateMap[key].push(log);
    });
    const duplicateKeys = new Set(
        Object.entries(duplicateMap)
            .filter(([, v]) => v.length > 1 && new Set(v.map(l => l.agent_id)).size > 1)
            .map(([k]) => k)
    );

    return (
        <div className="space-y-6" data-testid="shared-call-log-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Shared Call Log</h1>
                    <p className="text-muted-foreground text-sm">Track all student calls across CS and Mentors to avoid duplicate outreach</p>
                </div>
                {isAdmin && (
                    <Button variant="outline" size="sm" onClick={handleForceSync} disabled={syncing}
                        className="gap-2" data-testid="force-sync-calls-btn">
                        <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                        {syncing ? 'Syncing 3CX...' : 'Force Sync 3CX'}
                    </Button>
                )}
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="pt-4">
                    <div className="flex flex-wrap gap-3 items-end">
                        <div className="space-y-1">
                            <Label className="text-xs">From</Label>
                            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                                className="w-[140px] h-9 text-sm" data-testid="call-log-date-from" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">To</Label>
                            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                                className="w-[140px] h-9 text-sm" data-testid="call-log-date-to" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Department</Label>
                            <Select value={filterDept} onValueChange={setFilterDept}>
                                <SelectTrigger className="w-[130px] h-9 text-sm" data-testid="call-log-dept-filter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All (CS + Mentor)</SelectItem>
                                    <SelectItem value="cs">CS Only</SelectItem>
                                    <SelectItem value="mentor">Mentor Only</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {isAdmin && agents.length > 0 && (
                            <div className="space-y-1">
                                <Label className="text-xs">Agent</Label>
                                <Select value={filterAgent} onValueChange={setFilterAgent}>
                                    <SelectTrigger className="w-[170px] h-9 text-sm" data-testid="call-log-agent-filter">
                                        <SelectValue placeholder="All Agents" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Agents</SelectItem>
                                        {agents.map(a => (
                                            <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="space-y-1 flex-1 min-w-[200px]">
                            <Label className="text-xs">Search Student</Label>
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                                <Input placeholder="Student name or phone..."
                                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                    className="pl-8 h-9 text-sm" data-testid="call-log-search" />
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Duplicate Warning */}
            {duplicateKeys.size > 0 && (
                <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm">
                    <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    <span><strong>{duplicateKeys.size} student(s)</strong> were called by multiple agents on the same day. These rows are highlighted below.</span>
                </div>
            )}

            {/* Call Log Table */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Phone className="h-4 w-4" /> Call History ({logs.length} calls)
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex justify-center py-10">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
                        </div>
                    ) : logs.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No call logs found for the selected period.</p>
                    ) : (
                        <ScrollArea className="max-h-[60vh]">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[140px]">Date & Time</TableHead>
                                        <TableHead>Student</TableHead>
                                        <TableHead>Phone</TableHead>
                                        <TableHead>Called By</TableHead>
                                        <TableHead className="text-right">Duration</TableHead>
                                        <TableHead>Direction</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {logs.map((log, i) => {
                                        const isDuplicate = duplicateKeys.has(`${log.student_phone}_${log.date}`);
                                        const dept = getDept(log.agent_role);
                                        return (
                                            <TableRow key={i} className={isDuplicate ? 'bg-amber-500/5 border-l-2 border-l-amber-500' : ''}>
                                                <TableCell className="text-xs font-mono">
                                                    {log.call_time ? new Date(log.call_time).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : log.date}
                                                </TableCell>
                                                <TableCell className="font-medium text-sm">{log.student_name || '—'}</TableCell>
                                                <TableCell className="font-mono text-xs">{log.student_phone}</TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                                                            dept === 'cs' ? 'bg-emerald-600' : 'bg-violet-600'
                                                        }`}>
                                                            {log.agent_name?.charAt(0) || '?'}
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-sm">{log.agent_name}</p>
                                                            <Badge className={`text-[9px] px-1 py-0 ${DEPT_COLORS[dept]}`}>{dept.toUpperCase()}</Badge>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right font-mono text-xs">
                                                    {log.duration ? `${Math.floor(log.duration / 60)}:${String(log.duration % 60).padStart(2, '0')}` : '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className="text-[10px]">
                                                        {log.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                                                    </Badge>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </ScrollArea>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
